import { SupplierProduct } from "@domain/suppliers/models/Supplier";
import { Product } from "@domain/product/models/Product";
import { ProductMode } from "@domain/product/models/ProductMode";
import { SupplierProductDTO, ProductDTO } from "../dto/ProductDTO";
import { ShipmentMethodMapper } from "@infrastructure/purchasing/mappers/ShipmentMethodMapper";
import { ProductListingMapper } from "./ProductListingMapper";
import { ProductComponentMapper } from "./ProductComponentMapper";
import { CreateProductDTO } from "../dto/CreateProductDTO";
import { CollectionMapper } from "./CollectionMapper";

export class ProductMapper {
  static toModel(dto: ProductDTO): Product {
    return {
      id: dto.id,
      ean: dto.ean,
      sku: dto.sku,
      customFields: dto.customFields,
      companyId: dto.company_id,
      idType: dto.id_type,
      title: dto.title,
      image: dto.image,
      description: dto.description,
      active: dto.active,
      purchasable: dto.purchasable,
      bundle: dto.bundle,
      mode: dto.mode as ProductMode,
      minimumDaysOfStockInReserve: dto.minimumDaysOfStockInReserve, 
      minimumOrderAmountDays: dto.minimumOrderAmountDays,
      productionDuration: dto.productionDuration,
      processingDuration: dto.processingDuration,
      defaultShipmentMethodId: dto.defaultShipmentMethodId,
      defaultShipmentMethod: dto.defaultShipmentMethod ? ShipmentMethodMapper.toModel(dto.defaultShipmentMethod) : undefined,
      forecastParameters: dto.forecastParameters,
      forecastGrade: dto.forecastGrade,
      listings: dto.listings?.map((l) => ProductListingMapper.toModel(l)),
      parts: dto.parts?.map((c) => ProductComponentMapper.toPartModel(c)),
      usedIn: dto.usedIn?.map((c) => ProductComponentMapper.toUsedInModel(c)),
      collections: dto.collectionProducts?.map((cp) => {
        return CollectionMapper.toModel(cp.collection);
      }),
      supplierLinks: dto.supplier_links?.map((l) => ({
        supplierId: l.supplier_id,
        isMain: l.is_main,
      })),
      supplierProducts: (dto.supplierProducts ?? dto.supplied_by)?.map((l) => ProductMapper.supplierProductDTOToModel(l)),
    };
  }

  private static supplierProductDTOToModel(dto: SupplierProductDTO): SupplierProduct {
    return {
      id: dto.id,
      product: ProductMapper.toModel(dto.product),
      supplierId: dto.supplierId ?? dto.supplier_id,
      isMain: dto.isMain,
      purchasePrice: dto.purchasePrice ? parseFloat(dto.purchasePrice) : undefined,
      currency: dto.currency,
      minimumOrderQuantity: dto.minimumOrderQuantity ? parseFloat(dto.minimumOrderQuantity) : undefined,
      orderInMultiplesOf: dto.orderInMultiplesOf ? parseFloat(dto.orderInMultiplesOf) : undefined,
      minimumOrderQuantityType: dto.minimumOrderQuantityType,
    };
  }

  static toCreateDTO(model: Omit<Product, 'id'>): CreateProductDTO {
    return {
      ean: model.ean,
      sku: model.sku,
      customFields: model.customFields,
      title: model.title ?? '',
      image: model.image,
      description: model.description,
      active: model.active,
      purchasable: model.purchasable,
      mode: model.mode,
      minimumDaysOfStockInReserve: model.minimumDaysOfStockInReserve,
      minimumOrderAmountDays: model.minimumOrderAmountDays,
      productionDuration: model.productionDuration,
      processingDuration: model.processingDuration,
      defaultShipmentMethodId: model.defaultShipmentMethodId,
    };
  }
}
